import { isBoxLayout } from '@lessonup/pin-renderer';
import {
  isTextPinComponent,
  newOpenQuestionPin,
  newPin,
  newSlidePin,
  newTextPinComponent,
  newVector,
  newWordCloudPin,
  PinComponent,
  slideCoverLayout,
  slideDoubleColumnTextLayout,
  slideImageLeftLayout,
  slideImageRightLayout,
  slideImageTopLayout,
  slideTitleWithBodytextLayout,
  stringToProsemirror,
  TeacherPin,
  TextPinComponent,
} from '@lessonup/pins-shared';
import { SelectOption } from '@lessonup/ui-components';
import { TFunction } from 'i18next';
import { compact } from 'lodash';
import {
  GeneratedLessonV2DocumentPartial,
  GeneratedSlideV2,
  GenerateMultipleChoiceQuestionsPayload,
  GeneratePollPayload,
  Language,
} from '../../../types/graphql';
import { createTranslatedTemplate } from '../../Editor/utils/createTranslatedTemplate';
import { MaiaLessonType, textListToHTMLList } from '../ChatGPTGeneratorFeature.utils';
import { GeneratedResultItem } from '../components/Wizard/ReviewStep/reviewStepUtils';
import {
  GeneratedLessonResponse,
  LessonResponse,
  OpenQuestionResponse,
  OptionalLessonSettings,
  PollResponse,
  QuizResponse,
  ResponseSelection,
  WordWebResponse,
} from '../model';

const defaultLanguage: Language = 'EN_UK';

const singleSlideOption = (count: number) => {
  return { label: `${count}`, value: count };
};

export const giveSelectionOptions = (selectCount: number, startCount = 1): SelectOption<number>[] => {
  const slideOptions = [];
  for (let i = startCount; i <= selectCount; i++) {
    slideOptions.push(singleSlideOption(i));
  }
  return slideOptions;
};

export const generateTempResultId = (string: string) => Buffer.from(string).toString('base64');

export function mapGraphQLtoQuizResponse(document: GenerateMultipleChoiceQuestionsPayload): QuizResponse {
  const list = mapQuizResponse(document);
  return {
    type: 'quiz',
    list: compact(list),
  };
}

export function mapGraphQLtoPollResponse(document: GeneratePollPayload): PollResponse {
  const list = mapPollResponse(document);
  return {
    type: 'poll',
    list: compact(list),
  };
}

export function mapGraphQLtoGeneratedLessonResponse(
  document: GeneratedLessonV2DocumentPartial | undefined,
  optionalSettings: OptionalLessonSettings,
  t: TFunction,
  outputLanguage: Language
): GeneratedLessonResponse {
  const slides =
    document?.slides?.map((slide, index) => {
      return {
        pin: convertGeneratedSlideV2ToPin(slide, document),
        tempId: `slide-${index}`,
        selected: false,
      };
    }) ?? [];

  const [coverSlide, ...remainingSlides] = slides;
  if (coverSlide) {
    coverSlide.tempId = 'cover-slide';
  }

  const fontSettings: Partial<TextPinComponent['settings']> = {
    fontFamily: document?.fontFamily ?? undefined,
  };
  const template = createTranslatedTemplate();
  // const slideLayouts = template.layouts.slidePinLayouts;
  const openQuestionLayout = template.layouts.openQuestionLayouts.find((layout) => layout.key === 'TITLE_ONLY');
  const wordCloudLayout = template.layouts.wordCloudLayouts.find((layout) => layout.key === 'TITLE_ONLY');
  // const slideLayout = slideLayouts.find((slide) => slide.key === 'TITLE_TOP_WITH_BODYTEXT');

  const previousKnowledgeSlide =
    optionalSettings.enablePreviousKnowledge && wordCloudLayout && coverSlide
      ? [activatePreviousKnowledgeSlide(document?.introQuestion ?? undefined)].map((item) => ({
          pin: newPin(
            newWordCloudPin({
              key: wordCloudLayout.key,
              backgroundColor: document?.accentBackgroundColor ?? wordCloudLayout.backgroundColor,
              backgroundImage: wordCloudLayout.backgroundImage,
              backgroundTransparency: wordCloudLayout.backgroundTransparency,
              pinComponents: [],
              layoutParts: {
                ...wordCloudLayout.layoutParts,
                TITLE: createMaiaTextPinComponent({
                  tempId: item.tempId,
                  text: item.word,
                  bold: true,
                  layoutTextPinComponent: wordCloudLayout.layoutParts.TITLE,
                  fontSettings,
                }),
              },
            })
          ),
          tempId: 'activate-previous-knowledge-slide',
          selected: false,
        }))
      : [];

  const exitTicketSlides =
    optionalSettings.enableExitTicket && openQuestionLayout
      ? getExitTicketSlideData(t, optionalSettings.enableTeacherNotes, outputLanguage).map((item) => ({
          pin: newPin(
            newOpenQuestionPin({
              key: openQuestionLayout.key,
              backgroundColor: document?.accentBackgroundColor ?? openQuestionLayout.backgroundColor,
              backgroundImage: openQuestionLayout.backgroundImage,
              backgroundTransparency: openQuestionLayout.backgroundTransparency,
              pinComponents: [],
              layoutParts: {
                ...openQuestionLayout.layoutParts,
                TITLE: createMaiaTextPinComponent({
                  bold: true,
                  tempId: item.tempId,
                  text: item.question,
                  layoutTextPinComponent: openQuestionLayout.layoutParts.TITLE,
                  fontSettings,
                }),
              },
            })
          ),
          tempId: item.tempId,
          selected: false,
        }))
      : [];

  const allSlides = compact([coverSlide, ...previousKnowledgeSlide, ...remainingSlides, ...exitTicketSlides]);

  return {
    name: document?.name ?? '',
    list: allSlides,
    settings: optionalSettings,
  };
}

export const convertGeneratedSlideV2ToPin = (
  slide: GeneratedSlideV2,
  document: GeneratedLessonV2DocumentPartial | undefined
): TeacherPin => {
  const titlePart = slide.layoutParts.find((part) => part.type === 'TITLE');
  const subtitle1Part = slide.layoutParts.find((part) => part.type === 'SUBTITLE1');
  const subtitle2Part = slide.layoutParts.find((part) => part.type === 'SUBTITLE2');
  const bodyText1Part = slide.layoutParts.find((part) => part.type === 'BODYTEXT1');
  const bodyText2Part = slide.layoutParts.find((part) => part.type === 'BODYTEXT2');
  const image1Part = slide.layoutParts.find((part) => part.type === 'IMAGE1');
  const backgroundColor = document?.backgroundColor ?? undefined;
  const fontSettings: Partial<TextPinComponent['settings']> = {
    fontFamily: document?.fontFamily ?? undefined,
    fontColor: document?.fontColor ?? undefined,
  };

  switch (slide.layout) {
    case 'COVER':
      return newPin(
        newSlidePin(
          slideCoverLayout({
            title: titlePart
              ? {
                  content: stringToProsemirror(titlePart.content, { bold: true }),
                  settings: fontSettings,
                }
              : undefined,
            body: bodyText1Part
              ? {
                  content: stringToProsemirror(bodyText1Part.content),
                  settings: fontSettings,
                }
              : undefined,
            backgroundColor: backgroundColor,
          })
        )
      );

    case 'BODYTEXT_TWO_COLUMNS':
      return newPin(
        newSlidePin(
          slideDoubleColumnTextLayout({
            title: titlePart
              ? {
                  content: stringToProsemirror(titlePart.content, { bold: true }),
                  settings: fontSettings,
                }
              : undefined,
            subTitleOne: subtitle1Part
              ? {
                  content: stringToProsemirror(subtitle1Part.content),
                  settings: fontSettings,
                }
              : undefined,
            textOne: bodyText1Part
              ? {
                  content: stringToProsemirror(bodyText1Part.content),
                  settings: fontSettings,
                }
              : undefined,
            subTitleTwo: subtitle2Part
              ? {
                  content: stringToProsemirror(subtitle2Part.content),
                  settings: fontSettings,
                }
              : undefined,
            textTwo: bodyText2Part
              ? {
                  content: stringToProsemirror(bodyText2Part.content),
                  settings: fontSettings,
                }
              : undefined,
            backgroundColor: backgroundColor,
          })
        )
      );

    case 'IMAGE_TOP':
      return newPin(
        newSlidePin(
          slideImageTopLayout({
            title: titlePart
              ? {
                  content: stringToProsemirror(titlePart.content, { bold: true }),
                  settings: fontSettings,
                }
              : undefined,
            imageUrl: image1Part ? image1Part.content : undefined,
            backgroundColor: backgroundColor,
          })
        )
      );

    case 'IMAGE_LEFT':
      return newPin(
        newSlidePin(
          slideImageLeftLayout({
            title: titlePart
              ? {
                  content: stringToProsemirror(titlePart.content, { bold: true }),
                  settings: fontSettings,
                }
              : undefined,
            body: bodyText1Part
              ? {
                  content: stringToProsemirror(bodyText1Part.content),
                  settings: fontSettings,
                }
              : undefined,
            imageUrl: image1Part ? image1Part.content : undefined,
            backgroundColor: backgroundColor,
          })
        )
      );

    case 'IMAGE_RIGHT':
      return newPin(
        newSlidePin(
          slideImageRightLayout({
            title: titlePart
              ? {
                  content: stringToProsemirror(titlePart.content, { bold: true }),
                  settings: fontSettings,
                }
              : undefined,
            body: bodyText1Part
              ? {
                  content: stringToProsemirror(bodyText1Part.content),
                  settings: fontSettings,
                }
              : undefined,
            imageUrl: image1Part ? image1Part.content : undefined,
            backgroundColor: backgroundColor,
          })
        )
      );

    default:
      // Fallback to title with body text layout
      return newPin(
        newSlidePin(
          slideTitleWithBodytextLayout({
            title: titlePart
              ? {
                  content: stringToProsemirror(titlePart.content, { bold: true }),
                  settings: fontSettings,
                }
              : undefined,
            body: bodyText1Part
              ? {
                  content: stringToProsemirror(bodyText1Part.content),
                  settings: fontSettings,
                }
              : undefined,
            backgroundColor: backgroundColor,
          })
        )
      );
  }
};

export const mapLessonResponseToGeneratedResultList = (
  list: LessonResponse['list'],
  lessonType: MaiaLessonType
): GeneratedResultItem[] => {
  return compact(
    list.map((item) => {
      if (item.type === 'slide') {
        const slide: GeneratedResultItem = {
          tempId: item.tempId,
          phaseNumber: item.phaseNumber,
          content: {
            type: 'slide',
            header: item.header,
            layout: item.layout,
            text: lessonType === 'contextLesson' ? textListToHTMLList(item.text) : item.text,
            note: item.note,
          },
        };
        return slide;
      }
      if (item.type === 'openQuestion') {
        const openQuestion: GeneratedResultItem = {
          tempId: item.tempId,
          phaseNumber: item.phaseNumber,
          content: {
            type: 'openQuestion',
            question: item.question,
            note: item.note,
          },
        };
        return openQuestion;
      }
      if (item.type === 'wordweb') {
        const wordweb: GeneratedResultItem = {
          tempId: item.tempId,
          phaseNumber: item.phaseNumber,
          content: {
            type: 'wordweb',
            word: item.word,
          },
        };
        return wordweb;
      }
      console.warn('unknown type', item);
    })
  );
};

const mapPollResponse = (document: GeneratePollPayload): ResponseSelection<PollResponse> => {
  return document.polls.map((question, index) => ({
    tempId: `poll-question-${index}-${simpleIdentifierForTitle(question.statement)}`,
    statement: question.statement,
    options: question.answers,
  }));
};

const mapQuizResponse = (document: GenerateMultipleChoiceQuestionsPayload): ResponseSelection<QuizResponse> => {
  return document.questions.map((question, index) => ({
    tempId: `quiz-question-${index}-${simpleIdentifierForTitle(question.question)}`,
    question: question.question,
    answers: question.answers,
    correctAnswer: question.correctAnswers,
  }));
};

const activatePreviousKnowledgeSlide = (word?: string): WordWebResponse => ({
  tempId: 'activate-previous-knowledge-ticket-1',
  type: 'wordweb',
  word: word ?? '',
  phaseNumber: 1000,
});

const getExitTicketSlideData = (
  t: TFunction,
  notesEnabled: boolean,
  outputLanguage: Language
): OpenQuestionResponse[] => {
  if (outputLanguage === undefined || outputLanguage === 'EN') {
    outputLanguage = defaultLanguage;
  }

  const translatedExitTicket = exitTicketTranslations[outputLanguage];

  return [
    {
      tempId: 'exit-ticket-1',
      type: 'openQuestion',
      question: translatedExitTicket.question1 ?? t('exitTicketQuestions.question1'),
      note: notesEnabled ? (translatedExitTicket.question1Note ?? t('exitTicketQuestions.question1Note')) : '',
      phaseNumber: 4000,
    },
    {
      tempId: 'exit-ticket-2',
      type: 'openQuestion',
      question: translatedExitTicket.question2 ?? t('exitTicketQuestions.question2'),
      note: notesEnabled ? (translatedExitTicket.question2Note ?? t('exitTicketQuestions.question2Note')) : '',
      phaseNumber: 4000,
    },
    {
      tempId: 'exit-ticket-3',
      type: 'openQuestion',
      question: translatedExitTicket.question3 ?? t('exitTicketQuestions.question3'),
      note: notesEnabled ? (translatedExitTicket.question3Note ?? t('exitTicketQuestions.question3Note')) : '',
      phaseNumber: 4000,
    },
  ];
};

const exitTicketTranslations: Record<
  Exclude<Language, 'EN'>,
  {
    question1: string;
    question1Note: string;
    question2: string;
    question2Note: string;
    question3: string;
    question3Note: string;
  }
> = {
  // English (UK)
  EN_UK: {
    question1: 'Write down 3 things you learned in this lesson.',
    question1Note:
      'Have students enter three things they learned in this lesson. With this, they can indicate their own learning efficiency of this lesson.',
    question2: 'Write down 2 things you want to know more about.',
    question2Note:
      'Here, students enter two things they would like to know more about. This not only increases involvement but also gives them more ownership.',
    question3: "Ask 1 question about something you haven't quite understood yet.",
    question3Note:
      'The students indicate here (in question form) with which part of the material they still have difficulty. For the teacher, this not only provides insight into the extent to which the students understand/master the material, but also a good starting point for the next lesson.',
  },
  // English (US)
  EN_US: {
    question1: 'Write down 3 things you learned in this lesson.',
    question1Note:
      'Have students enter three things they learned in this lesson. With this, they can indicate their own learning efficiency of this lesson.',
    question2: 'Write down 2 things you want to know more about.',
    question2Note:
      'Here, students enter two things they would like to know more about. This not only increases involvement but also gives them more ownership.',
    question3: "Ask 1 question about something you haven't quite understood yet.",
    question3Note:
      'The students indicate here (in question form) with which part of the material they still have difficulty. For the teacher, this not only provides insight into the extent to which the students understand/master the material, but also a good starting point for the next lesson.',
  },
  // Dutch
  NL: {
    question1: 'Schrijf 3 dingen op die je deze les hebt geleerd.',
    question1Note:
      'De leerlingen voeren hier drie dingen in die ze in deze les hebben geleerd. Hiermee geven ze aan wat hun eigen leerrendement van deze les is.',
    question2: 'Schrijf 2 dingen op waarover je meer wilt weten.',
    question2Note:
      'De leerlingen voeren hier twee dingen in waarover ze meer zouden willen weten. Hiermee vergroot je niet alleen betrokkenheid, maar geef je hen ook meer eigenaarschap.',
    question3: 'Stel 1 vraag over iets dat je nog niet zo goed hebt begrepen.',
    question3Note:
      'De leerlingen geven hier (in vraagvorm) aan met welk onderdeel van de stof ze nog moeite. Voor de docent biedt dit niet alleen inzicht in de mate waarin de stof de leerlingen begrijpen/beheersen, maar ook een goed startpunt voor een volgende les.',
  },
  // German
  DE: {
    question1: 'Schreibe 3 Dinge auf, die Du in dieser Lektion gelernt hast.',
    question1Note:
      'Die Schüler geben hier drei Dinge ein, die sie in dieser Lektion gelernt haben. Damit geben sie an, was ihr eigenes Lernergebnis dieser Lektion ist.',
    question2: 'Schreibe 2 Dinge auf, über die Du mehr wissen möchtest.',
    question2Note:
      'Die Schüler geben hier zwei Dinge ein, über die sie mehr wissen möchten. Damit erhöhen Sie nicht nur das Engagement, sondern geben Sie den Schülern auch mehr Eigenverantwortung.',
    question3: 'Stelle Dir 1 Frage zu etwas, das Du noch nicht so gut verstanden hast.',
    question3Note:
      'Die Schüler geben hier (in Form einer Frage) an, mit welchem Teil des Materials sie noch Schwierigkeiten haben. Für den Lehrer bietet dies nicht nur Einblick in das Verständnis/Niveau der Schüler, sondern auch einen guten Ausgangspunkt für die nächste Lektion.',
  },
  // French
  FR: {
    question1: 'Écrivez 3 choses que vous avez apprises lors de cette leçon.',
    question1Note:
      "Les élèves écrivent trois choses qu'ils ont apprises au cours de cette leçon. Cela leur permet de signaler leur propre rendement d'apprentissage de cette leçon.",
    question2: 'Écrivez 2 choses sur lesquelles vous souhaitez en savoir plus.',
    question2Note:
      'Les élèves écrivent deux choses sur lesquelles ils aimeraient en savoir plus. Cela non seulement augmente leur engagement, mais leur donne également plus de responsabilité.',
    question3: "Posez 1 question sur quelque chose que vous n'avez pas bien compris.",
    question3Note:
      "Les élèves indiquent (sous forme de question) la partie de la matière qui leur pose encore des difficultés. Pour l'enseignant, cela offre non seulement un aperçu du niveau de compréhension/maîtrise des élèves de la matière, mais aussi un bon point de départ pour la prochaine leçon.",
  },
  // Spanish
  ES: {
    question1: 'Escribe 3 cosas que hayas aprendido en esta lección',
    question1Note:
      'Los alumnos escriben aquí 3 cosas que han aprendido en esta lección. Al hacerlo, indican su propio aprendizaje de esta lección.',
    question2: 'Escribe 2 cosas sobre las que te gustaría aprender más.',
    question2Note:
      'Aquí los alumnos introducen dos cosas sobre las que les gustaría saber más. Esto no sólo aumenta el compromiso, sino también el nivel de involucración.',
    question3: 'Haz 1 pregunta sobre algo que aún no has entendido muy bien',
    question3Note:
      'Aquí los alumnos indican (en forma de pregunta) con qué parte del material aún tienen dificultades. Para el profesor, esto no sólo proporciona una idea de hasta qué punto los alumnos comprenden/manejan el material, sino que es también un buen punto de partida para una lección posterior.',
  },
  // Welsh
  CY: {
    question1: 'Ysgrifennwch 3 pheth a ddysgoch yn y wers hon.',
    question1Note:
      "Gofynnwch i fyfyrwyr nodi tri pheth a ddysgon nhw yn y wers hon. Gyda hyn, gallant nodi effeithlonrwydd eu dysgu eu hunain o'r wers hon.",
    question2: 'Ysgrifennwch 2 beth yr hoffech wybod mwy amdanynt.',
    question2Note:
      'Yma, mae myfyrwyr yn nodi dau beth y byddent yn hoffi gwybod mwy amdanynt. Mae hyn nid yn unig yn cynyddu cyfranogiad ond hefyd yn rhoi mwy o berchnogaeth iddynt.',
    question3: "Gofynnwch 1 cwestiwn am rywbeth nad ydych wedi'i ddeall yn iawn eto.",
    question3Note:
      'Mae’r myfyrwyr yn nodi yma (mewn ffurf cwestiwn) pa ran o’r deunydd y mae ganddynt anhawster ag ef o hyd. I’r athro, mae hyn nid yn unig yn darparu mewnwelediad i’r graddau y mae’r myfyrwyr yn deall/meistroli’r deunydd ond hefyd yn fan cychwyn da ar gyfer y wers nesaf.',
  },
  // Gaelic (Scottish Gaelic)
  GD: {
    question1: 'Sgrìobh sìos 3 rud a dh’ionnsaich thu san leasan seo.',
    question1Note:
      'Leig le oileanaich trì rudan a dh’ionnsaich iad san leasan seo a chur a-steach. Le seo, faodaidh iad comharrachadh dè cho math ’s a thuig iad an leasan seo.',
    question2: 'Sgrìobh sìos 2 rud a bu mhath leat barrachd fhaighinn a-mach mu dheidhinn.',
    question2Note:
      'An seo, bidh oileanaich a’ cur a-steach dà rud a bu mhath leotha barrachd fhaighinn a-mach mu dheidhinn. Bidh seo chan ann a-mhàin a’ meudachadh an com-pàirteachadh, ach a’ toirt barrachd seilbh dhaibh cuideachd.',
    question3: 'Cuir 1 cheist mu rudeigin nach do thuig thu gu math fhathast.',
    question3Note:
      'An seo, tha na h-oileanaich a’ comharrachadh (ann an cruth ceist) dè an roinn den stuth leis a bheil iad fhathast a’ strì. Dha’n tidsear, tha seo chan ann a-mhàin a’ toirt sealladh air dè cho math ’s a tha an luchd-ionnsachaidh a’ tuigsinn/am maighstireachd air an stuth, ach cuideachd mar thoiseach tòiseachaidh math airson an ath leasan.',
  },
  // Portuguese
  PT: {
    question1: 'Partilha 3 aprendizagens desta aula.',
    question1Note:
      'Peça aos alunos que escrevam três aprendizagens que aprenderam nesta aula. Com isso, eles podem indicar a sua própria eficiência de aprendizagem nesta aula.',
    question2: 'Partilha duas coisas sobre as quais queres saber mais.',
    question2Note:
      'Aqui, os alunos escrevem duas coisas sobre as quais gostariam de saber mais. Isso não apenas aumenta o envolvimento, mas também lhes dá mais autonomia.',
    question3: 'Que pergunta gostarias de fazer para aprofundar ainda mais o teu conhecimento?',
    question3Note:
      'Os alunos indicam aqui (em forma de pergunta) com qual parte do material ainda têm dificuldade. Para o/a professor/a, isso não apenas fornece uma visão sobre o nível de compreensão dos alunos, mas também um bom ponto de partida para a próxima aula.',
  },
  // Italian
  IT: {
    question1: 'Scrivi 3 cose che hai imparato in questa lezione.',
    question1Note:
      'Per confermare che gli studenti abbiano prestato attenzione e che ció che hanno capito sia corretto.',
    question2: 'Scrivi 2 cose di cui vuoi sapere di più.',
    question2Note: 'Per aumentare il senso di coinvolgimento e di autonomia degli studenti.',
    question3: 'Fai 1 domanda su qualcosa che non hai ancora capito bene.',
    question3Note:
      "Offre un'idea generale del livello di comprensione degli studenti. Utile a identificare parti che generano confusione agli studenti e come punto di partenza per lezioni successive.",
  },
  // Chinese
  ZH: {
    question1: '写下你在这节课中学到的3件事。',
    question1Note: '让学生输入他们在这节课中学到的三件事。这可以帮助他们评估自己的学习效果。',
    question2: '写下2件你想了解更多的事情。',
    question2Note: '在这里，学生输入他们想了解更多的两件事。这不仅可以增加他们的参与感，还能增强他们的学习主动性。',
    question3: '提出1个你还没有完全理解的问题。',
    question3Note:
      '学生在这里（以问题形式）指出他们仍然感到困难的知识点。这不仅能帮助教师了解学生的掌握情况，还可以作为下一节课的切入点。',
  },
  // Mongolian
  MN: {
    question1: 'Энэ хичээлээр сурсан 3 зүйлээ бичнэ үү.',
    question1Note:
      'Сурагчид энэ хичээлээр сурсан гурван зүйлээ бичнэ. Ингэснээр тэд өөрийн суралцах үр дүнгээ тодорхойлж чадна.',
    question2: 'Илүү ихийг мэдэхийг хүсч буй 2 зүйлээ бичнэ үү.',
    question2Note:
      'Энд сурагчид илүү ихийг мэдэхийг хүсч буй хоёр зүйлээ бичнэ. Энэ нь тэдний оролцоог нэмэгдүүлж, хичээлдээ илүү эзэн болж өгнө.',
    question3: 'Ойлгоогүй байгаа 1 асуултыг бичнэ үү.',
    question3Note:
      'Сурагчид энд (асуултын хэлбэрээр) ойлгоход хэцүү байгаа хэсгээ илэрхийлнэ. Энэ нь багшид сурагчдын ойлголтын түвшинг үнэлэхэд туслах бөгөөд дараагийн хичээлээ төлөвлөхөд ашигтай байж болно.',
  },
  // Hindi
  HI: {
    question1: 'इस पाठ में आपने सीखी गई 3 चीजें लिखें।',
    question1Note: 'छात्र यहां इस पाठ में सीखी गई तीन चीजें दर्ज करें। इससे वे अपने सीखने की दक्षता को दर्शा सकते हैं।',
    question2: '2 चीजें लिखें जिनके बारे में आप और अधिक जानना चाहते हैं।',
    question2Note:
      'यहां छात्र दो चीजें दर्ज करेंगे जिनके बारे में वे अधिक जानना चाहते हैं। इससे उनकी भागीदारी बढ़ेगी और वे अधिक जिम्मेदार महसूस करेंगे।',
    question3: 'ऐसा 1 प्रश्न पूछें जिसे आप अभी तक पूरी तरह से नहीं समझ पाए हैं।',
    question3Note:
      'छात्र यहां (प्रश्न के रूप में) बताएंगे कि उन्हें किस भाग को समझने में कठिनाई हो रही है। शिक्षक के लिए यह उनकी समझ का मूल्यांकन करने और अगली कक्षा के लिए योजना बनाने में सहायक होगा।',
  },
  // Japanese
  JA: {
    question1: 'このレッスンで学んだ3つのことを書きましょう。',
    question1Note: '生徒がこのレッスンで学んだ3つのことを入力します。これにより、彼らは自分の学習効果を確認できます。',
    question2: 'もっと知りたい2つのことを書きましょう。',
    question2Note: '生徒がもっと知りたい2つのことを入力します。これにより、関与が高まり、学習の主体性が向上します。',
    question3: 'まだよく理解できていないことについて1つ質問をしてください。',
    question3Note:
      '生徒はここで（質問形式で）まだ理解が不十分な部分を示します。教師にとって、これは生徒の理解度を評価し、次の授業の計画を立てるための良い指標になります。',
  },
};

function simpleIdentifierForTitle(title: string) {
  return title.toLowerCase().substring(0, 15).replace(/\s/, '_');
}

interface PassPromptBetweenMaiaModesParams {
  lessonType: MaiaLessonType;
  setLessonType: React.Dispatch<React.SetStateAction<MaiaLessonType | undefined>>;
  prompt: string | undefined;
  setTempSavedPrompt: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export function passPromptBetweenMaiaModes({
  prompt,
  lessonType,
  setLessonType,
  setTempSavedPrompt,
}: PassPromptBetweenMaiaModesParams) {
  setLessonType(lessonType);
  setTempSavedPrompt(prompt);
}

interface MaiaTextPinComponentProps {
  text: string;
  tempId: string;
  layoutTextPinComponent?: PinComponent;
  position?: { x: number; y: number };
  bold?: boolean;
  fontSettings?: Partial<TextPinComponent['settings']>;
}

const createMaiaTextPinComponent = ({
  text,
  layoutTextPinComponent,
  tempId,
  position,
  bold,
  fontSettings,
}: MaiaTextPinComponentProps) => {
  if (layoutTextPinComponent && isTextPinComponent(layoutTextPinComponent)) {
    const newTextPinComponent = {
      ...layoutTextPinComponent,
      id: tempId,
      settings: {
        ...layoutTextPinComponent.settings,
        ...fontSettings,
        tipTapContent: bold ? stringToProsemirror(text, { bold }) : stringToProsemirror(text),
      },
    };
    if (position && isBoxLayout(newTextPinComponent.layout)) {
      newTextPinComponent.layout = { ...newTextPinComponent.layout, position: newVector(position.x, position.y) };
    }
    return newTextPinComponent;
  } else {
    return newTextPinComponent({
      content: stringToProsemirror(text),
      settings: fontSettings,
    });
  }
};
